import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { cn } from "../../lib/cn";
import { inputStyles } from "./Input";
import { usePopoverId } from "./Popover";
import { Select } from "./Select";
import { DropdownPortal, useDropdownPanel } from "./useDropdownPanel";

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const DAY_OPTIONS = DAYS.map((day) => ({
  value: String(day),
  label: `Day ${day}`,
}));

export function formatDayOfMonthDisplay(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "Pick a day";
  }
  return `Day ${value}`;
}

function DayOfMonthGrid({ value, onChange, disabled = false, className }) {
  return (
    <div
      className={cn("grid grid-cols-7 gap-1", className)}
      role="radiogroup"
      aria-label="Day of month"
    >
      {DAYS.map((day) => {
        const active = value === day;
        return (
          <button
            key={day}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange?.(day)}
            className={cn(
              "h-9 rounded-lg text-sm font-medium transition-colors",
              active
                ? "bg-brand text-white shadow-sm"
                : "border border-border bg-card text-foreground hover:bg-muted/70",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            {day}
          </button>
        );
      })}
    </div>
  );
}

export function DayOfMonthPicker({
  value,
  onChange,
  disabled = false,
  className,
  variant = "grid",
}) {
  if (variant === "select") {
    return (
      <Select
        value={String(value)}
        disabled={disabled}
        onChange={(next) => onChange?.(Number(next))}
        options={DAY_OPTIONS}
        aria-label="Day of month"
        className={className}
      />
    );
  }

  if (variant === "picker") {
    return (
      <DayOfMonthPickerPopover
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={className}
      />
    );
  }

  return (
    <DayOfMonthGrid
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={className}
    />
  );
}

function DayOfMonthPickerPopover({
  value,
  onChange,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const panelId = usePopoverId();
  const { triggerRef, panelRef, position } = useDropdownPanel(open, setOpen);

  const openPanel = () => {
    if (disabled) return;
    setOpen(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={panelId}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={cn(
          inputStyles,
          "flex w-full items-center justify-between gap-2 text-left cursor-pointer",
          open && "ring-2 ring-ring border-transparent",
          disabled && "cursor-not-allowed",
          className,
        )}
      >
        <span className={cn(value == null && "text-muted-foreground")}>
          {formatDayOfMonthDisplay(value)}
        </span>
        <CalendarDays size={16} className="shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <DropdownPortal>
          <div
            ref={panelRef}
            data-picker-dropdown
            role="dialog"
            aria-labelledby={panelId}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: Math.max(position.width, 288),
              zIndex: 60,
            }}
            className="rounded-xl border border-border bg-card p-3 shadow-lg"
          >
            <DayOfMonthGrid
              value={value}
              disabled={disabled}
              onChange={(nextDay) => {
                onChange?.(nextDay);
                setOpen(false);
              }}
            />
          </div>
        </DropdownPortal>
      )}
    </>
  );
}
